/**
 * WebCodecs pipeline for Wave. Two exports:
 *  - `probeWebCodecs()` — runtime feature probe (memoised).
 *  - `transcodeToMp4WithWebCodecs()` — the real encode path used when the
 *    probe says H.264 encoding is available AND the source can be decoded by
 *    an HTMLVideoElement. Falls through to the caller's ffmpeg fallback via
 *    a typed `WebCodecsUnsupportedError` when the source can't be decoded.
 *
 * Chrome / Edge / Safari 16.4+ ship VideoEncoder with `avc1.42E01E` (H.264
 * Baseline 3.0); Firefox is behind a flag as of 2026. Wave keeps its
 * ffmpeg-wasm mpeg4 fallback for browsers that can't reach WebCodecs, so the
 * probe never blocks — it just publishes what's available so the encode path
 * can prefer WebCodecs when it can. See `project-transcode-backend`.
 */

import { deleteFile, mountInputFile, readFile, runFFmpeg } from './ffmpeg';
import { type MediaInfo } from './inspect';
import { openMuxer, type Mp4MuxHandle } from './mp4-mux';
import { createGpuToneMapper } from './tonemap-gl';
import { toneMapFilter, transposeFilters, type AudioEncodeSettings, type VideoEncodeSettings } from './plan';

export interface WebCodecsSupport {
  /** True when the browser exposes the `VideoEncoder` API at all. */
  hasVideoEncoder: boolean;
  /** True when the browser accepts an avc1.42E01E (H.264 Baseline) config. */
  h264Baseline: boolean;
  /** True when the browser accepts avc1.4D401E (H.264 Main 3.0). */
  h264Main: boolean;
  /** True when the browser accepts avc1.640028 (H.264 High 4.0). */
  h264High: boolean;
  /** True when the browser accepts a hev1.2.4.L120.B0 (HEVC Main 10) config —
   *  needed to preserve 10-bit HDR through the WebCodecs pipeline. Available
   *  on Safari, and on Chrome/Edge when the OS exposes an HEVC hardware
   *  encoder. Falsy on Linux and most Windows machines without hw HEVC. */
  hevcMain10: boolean;
  /** True when the browser accepts aac-lc audio via AudioEncoder. */
  aacLc: boolean;
  /** True when hardware acceleration was granted for any H.264 config. */
  hardwareAccelerated: boolean;
  /** Normalised source video codecs the browser can decode via an
   *  `HTMLVideoElement`. Populated by asking the element `canPlayType` for
   *  each candidate — probed at runtime rather than guessed by codec name,
   *  because e.g. HEVC decodes only where the OS has a decoder, and Firefox
   *  can't decode HEVC even where the OS does. Keys match the strings
   *  returned by `plan.ts::normalizeCodec` for consistency. */
  browserDecodableVideoCodecs: string[];
}

const EMPTY: WebCodecsSupport = {
  hasVideoEncoder: false,
  h264Baseline: false,
  h264Main: false,
  h264High: false,
  hevcMain10: false,
  aacLc: false,
  hardwareAccelerated: false,
  browserDecodableVideoCodecs: [],
};

let cached: Promise<WebCodecsSupport> | null = null;

export function probeWebCodecs(): Promise<WebCodecsSupport> {
  if (cached) return cached;
  cached = (async () => {
    const g = globalThis as unknown as {
      VideoEncoder?: {
        isConfigSupported: (cfg: VideoEncoderConfig) => Promise<{
          supported?: boolean;
          config?: VideoEncoderConfig & { hardwareAcceleration?: string };
        }>;
      };
      AudioEncoder?: {
        isConfigSupported: (cfg: AudioEncoderConfig) => Promise<{ supported?: boolean }>;
      };
    };
    if (!g.VideoEncoder || typeof g.VideoEncoder.isConfigSupported !== 'function') {
      return EMPTY;
    }
    const support: WebCodecsSupport = { ...EMPTY, hasVideoEncoder: true };
    const check = async (codec: string): Promise<{ ok: boolean; hw: boolean }> => {
      try {
        const res = await g.VideoEncoder!.isConfigSupported({
          codec,
          width: 1920,
          height: 1080,
          bitrate: 5_000_000,
          framerate: 30,
        });
        const ok = !!res.supported;
        const hw = (res.config?.hardwareAcceleration ?? '') === 'prefer-hardware';
        return { ok, hw };
      } catch {
        return { ok: false, hw: false };
      }
    };
    const [baseline, main, high, hevc10] = await Promise.all([
      check('avc1.42E01E'),
      check('avc1.4D401E'),
      check('avc1.640028'),
      // HEVC Main 10 Level 4.0 — enough for 1080p HDR at common bitrates.
      // Users on 4K HDR sources will still hit encoder limits at higher
      // resolutions; we accept the check as a Main-10-in-general probe.
      check('hev1.2.4.L120.B0'),
    ]);
    support.h264Baseline = baseline.ok;
    support.h264Main = main.ok;
    support.h264High = high.ok;
    support.hevcMain10 = hevc10.ok;
    support.hardwareAccelerated = baseline.hw || main.hw || high.hw || hevc10.hw;

    if (g.AudioEncoder && typeof g.AudioEncoder.isConfigSupported === 'function') {
      try {
        const res = await g.AudioEncoder.isConfigSupported({
          codec: 'mp4a.40.2',
          sampleRate: 48000,
          numberOfChannels: 2,
          bitrate: 192_000,
        });
        support.aacLc = !!res.supported;
      } catch {
        support.aacLc = false;
      }
    }

    support.browserDecodableVideoCodecs = probeBrowserDecode();
    return support;
  })();
  return cached;
}

/** For each candidate source codec, ask a real HTMLVideoElement whether it
 *  can play a representative MIME/codecs string. Anything the browser
 *  answers with 'probably' or 'maybe' is safe for the browser-decode path. */
function probeBrowserDecode(): string[] {
  if (typeof document === 'undefined') return [];
  let el: HTMLVideoElement;
  try { el = document.createElement('video'); } catch { return []; }
  // Container/codec strings picked so a positive answer really means the
  // decoder is present. `avc1.42E01E` is the most-supported H.264 profile;
  // `hev1.1.6.L93.B0` is HEVC Main; `av01.0.05M.08` is AV1 Main.
  const candidates: Array<[string, string]> = [
    ['h264', 'video/mp4; codecs="avc1.42E01E"'],
    ['hevc', 'video/mp4; codecs="hev1.1.6.L93.B0"'],
    ['vp8',  'video/webm; codecs="vp8"'],
    ['vp9',  'video/webm; codecs="vp9"'],
    ['av1',  'video/mp4; codecs="av01.0.05M.08"'],
  ];
  const out: string[] = [];
  for (const [codec, mime] of candidates) {
    const r = el.canPlayType(mime);
    if (r === 'probably' || r === 'maybe') out.push(codec);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Encode pipeline
// ─────────────────────────────────────────────────────────────────────────────

/** Thrown when the browser can't decode the source video via HTMLVideoElement.
 *  The caller (convert.ts) treats this as a signal to fall through to the
 *  full ffmpeg mpeg4 path — the file is still convertible, just not on the
 *  WebCodecs fast lane. */
export class WebCodecsUnsupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebCodecsUnsupportedError';
  }
}

/** Which decode strategy the pipeline should use for the video track:
 *  - `browser`: HTMLVideoElement + captureStream/rVFC. Fast (browser
 *    typically has a hardware decoder), works only when the browser can
 *    decode the source natively.
 *  - `ffmpeg`: segment-by-segment raw yuv420p extract via ffmpeg-wasm,
 *    then wrap raw bytes as `VideoFrame` and feed them to the same
 *    WebCodecs H.264 encoder. Slower on the decode leg (wasm) but keeps
 *    the H.264 output for sources the browser can't decode itself.
 *  - `webcodecs`: Path C (hwdecode.ts). Mediabunny demuxes, WebCodecs'
 *    VideoDecoder decodes (usually on the GPU), frames go to the same
 *    encoder. An order of magnitude faster than `ffmpeg`; falls back to it
 *    when the track can't be opened or the decoder fails partway. */
export type WebCodecsDecodeStrategy = 'browser' | 'ffmpeg' | 'webcodecs';

export interface WebCodecsTranscodeOptions {
  file: File;
  info: MediaInfo;
  support: WebCodecsSupport;
  /** Defaults to 'browser'. See `WebCodecsDecodeStrategy`. */
  decodeStrategy?: WebCodecsDecodeStrategy;
  /** Output video codec. Defaults to 'h264'. Set to 'hevc' only when the
   *  plan has confirmed HEVC Main 10 support AND wants to preserve HDR. */
  videoCodec?: 'h264' | 'hevc';
  /** Optional writable file stream (from `showSaveFilePicker`). When set,
   *  the muxer streams to disk instead of buffering the whole MP4 in RAM,
   *  and the returned blob is empty (the real output is on disk). Use for
   *  outputs expected to exceed ~500 MB. */
  streamTarget?: FileSystemWritableFileStream;
  /** Output size, bitrate, rotation and tone mapping chosen by the planner.
   *  Absent: encode at the source's display size with the default bitrate. */
  encode?: VideoEncodeSettings;
  /** Audio overrides from the planner (drop the track, cap its bitrate). */
  audio?: AudioEncodeSettings;
  /** Section of the source to keep, in seconds. Path B only. */
  trim?: { start: number; end: number } | null;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export interface WebCodecsTranscodeResult {
  blob: Blob;
  /** Which video profile the encoder actually accepted — H.264 baseline/
   *  main/high, or `hevcMain10` for the HDR-preserving path. */
  profile: 'baseline' | 'main' | 'high' | 'hevcMain10';
  /** True when the browser reported hardware acceleration for this encode. */
  hardwareAccelerated: boolean;
  /** Number of encoded frames the muxer received. */
  videoFrames: number;
  /** The decode path that actually ran: a `webcodecs` plan reports
   *  `ffmpeg` when it had to fall back. */
  decodeStrategy: WebCodecsDecodeStrategy;
  /** Number of AAC access units the muxer received. */
  audioFrames: number;
  /** Non-fatal issues surfaced during the encode — e.g. audio/video drift
   *  large enough to be perceptible. The output is still usable but the UI
   *  may want to flag them ("Audio might be slightly out of sync"). */
  warnings: string[];
}

/** Chunks the encoder buffers before we pause the source. Kept small so a
 *  crash mid-run doesn't lose more than ~1s of decoded frames. */
const ENCODE_QUEUE_HIGH = 24;
const ENCODE_QUEUE_LOW = 8;

/** How often to force a keyframe (in frames). ~2 seconds at 30 fps — good
 *  balance for seek performance and compression. */
const KEYFRAME_INTERVAL_FRAMES = 60;

/** Ceiling for playback rate. Chrome caps around 16x, but MediaStreamTrack-
 *  Processor and captureStream deliver frames on wall-clock cadence: any
 *  decoded frame the browser can't emit before the next one arrives is
 *  silently dropped. VP9 WebM at 8x was losing ~60% of frames in QA, which
 *  landed as a truncated video timeline in the output MP4. 2x keeps the
 *  encode noticeably faster than realtime while giving the decoder + capture
 *  pipeline enough headroom to preserve every frame. */
const MAX_PLAYBACK_RATE = 2;

export async function transcodeToMp4WithWebCodecs(
  opts: WebCodecsTranscodeOptions
): Promise<WebCodecsTranscodeResult> {
  if (!opts.support.hasVideoEncoder) {
    throw new WebCodecsUnsupportedError('VideoEncoder is not available in this browser.');
  }

  const videoCodec: 'h264' | 'hevc' = opts.videoCodec ?? 'h264';
  const profile = pickProfile(opts.support, videoCodec);
  const bitrate = opts.encode?.bitrate ?? pickBitrate(opts.info);
  // For anamorphic sources (SAR ≠ 1), the STORED width is smaller than the
  // DISPLAY width — e.g. an NTSC DVD stores 720×480 but should be shown at
  // 854×480. `displayWidth` is `storedWidth × SAR` from parseVideoStream.
  // Encoding at display dims lets us drop the SAR tag and skip anamorphic-
  // aware playback paths entirely — output is a square-pixel MP4 that plays
  // right everywhere.
  const srcWidth = opts.info.video?.displayWidth ?? opts.info.video?.width ?? 0;
  const srcHeight = opts.info.video?.height ?? 0;
  if (!srcWidth || !srcHeight) {
    throw new WebCodecsUnsupportedError('Source has no readable video dimensions.');
  }
  const rotation = opts.encode?.rotation ?? normaliseRotation(opts.info.video?.rotationDegrees ?? 0);
  // When the source declares a 90°/270° rotation, the display frame is the
  // stream frame with width and height swapped. Both decode strategies
  // deliver frames in DISPLAY orientation:
  //   - Path A: HTMLVideoElement + captureStream applies the rotation tag
  //     before frames leave the media pipeline, so the browser already sends
  //     us frames sized `videoWidth × videoHeight`.
  //   - Path B: ffmpeg's autorotate turns decoded frames by the source's tag,
  //     and any rotation the user added goes in as a `-vf transpose=…`.
  // With frames already rotated, the muxer's rotation tag stays at 0 — a
  // second, container-level rotation on top would flip everything back.
  const displaySwapped = rotation === 90 || rotation === 270;
  // The planner's size already has rotation and any downscale applied.
  const width = opts.encode?.width ?? (displaySwapped ? srcHeight : srcWidth);
  const height = opts.encode?.height ?? (displaySwapped ? srcWidth : srcHeight);
  const trim = opts.trim ?? null;
  if (trim && (opts.decodeStrategy ?? 'browser') === 'browser') {
    throw new WebCodecsUnsupportedError('Trimming needs the ffmpeg decode path.');
  }

  const strategy: WebCodecsDecodeStrategy = opts.decodeStrategy ?? 'browser';

  // Kick off audio extraction in parallel with the video encode setup.
  // Serialise it after decode for the `ffmpeg` strategy: sharing one wasm
  // engine between concurrent audio-extract and video-decode calls fights
  // for the same wasm heap and generally slows both.
  const audioOpts: AacExtractOptions = {
    trim,
    bitrateKbps: opts.audio?.bitrateKbps ?? null,
    channels: opts.audio?.channels ?? null,
  };
  const audioTask = opts.info.audio && !opts.audio?.remove
    ? (strategy !== 'browser'
        ? Promise.resolve().then(() => extractAacAudio(opts.file, opts.info, opts.signal, audioOpts))
        : extractAacAudio(opts.file, opts.info, opts.signal, audioOpts))
    : Promise.resolve(null);

  // For 'browser' strategy: load the source into an HTMLVideoElement so
  // the browser's own decoder handles demux + decode. For 'ffmpeg' strategy
  // we skip the video element entirely and let ffmpeg-wasm feed raw frames
  // to the encoder in encodeVideoTrack.
  const decoder = strategy === 'browser'
    ? await openVideoDecoder(opts.file, opts.signal)
    : null;
  // Path C: the module (and Mediabunny with it) loads only when needed. A
  // track the decoder can't take falls back to Path B before any work.
  const hw = strategy === 'webcodecs' ? await import('./hwdecode') : null;
  let hwSource = hw ? await hw.openHwDecode(opts.file) : null;
  const abortDecoder = () => decoder?.close();
  opts.signal?.addEventListener('abort', abortDecoder, { once: true });

  try {
    // Video-only progress dominates wall time; audio extraction reports at
    // most a small head-start (~5%) so the bar doesn't stall at 0.
    let audioReady = false;
    audioTask
      .then(() => { audioReady = true; })
      .catch(() => { /* handled below when we await it */ });

    const reportProgress = (videoFraction: number): void => {
      // Weight: 95% video, 5% audio prefetch.
      const combined = Math.min(1, videoFraction * 0.95 + (audioReady ? 0.05 : 0));
      opts.onProgress?.(combined);
    };

    let videoFrames = 0;
    let hardwareAccelerated = false;
    // Track the highest video PTS actually handed to the encoder so the A/V
    // drift check compares real durations, not `frameCount × 1/fps` (which
    // overshoots for VFR sources or captureStream at scaled playback rates).
    let lastVideoTimestampUs = 0;
    let lastVideoDurationUs = 0;

    const audioInfo = await audioTask;
    if (opts.signal?.aborted) throw abortError();
    if (audioInfo) audioReady = true;

    const encodeOnce = async (source: typeof hwSource) => {
      videoFrames = 0;
      lastVideoTimestampUs = 0;
      lastVideoDurationUs = 0;
      const muxer = openMuxer({
        videoCodec: videoCodec === 'hevc' ? 'hevc' : 'avc',
        streamTarget: opts.streamTarget,
        width,
        height,
        // Frames arrive pre-rotated (see `displaySwapped` above), so the
        // muxer's rotation tag stays at 0 — a second rotation applied by the
        // container on top of already-rotated pixels would flip everything
        // back to sideways.
        rotation: 0,
        // mp4-muxer requires an integer frame rate (or undefined). Non-integer
        // fps like 29.97 or 23.976 comes from broadcast-style sources; the
        // muxer only uses this value to round timestamps, so rounding to the
        // nearest integer is lossless enough for playback.
        frameRate: opts.info.video?.fps ? Math.round(opts.info.video.fps) : undefined,
        audio: audioInfo
          ? {
              sampleRate: audioInfo.sampleRate,
              numberOfChannels: audioInfo.channels,
            }
          : undefined,
      });

      // Feed audio first (order doesn't matter for mp4-muxer; but pushing
      // audio up-front means finalize() has less to sort at the end).
      if (audioInfo) writeAacFrames(muxer, audioInfo.frames);

      const encoderResult = await encodeVideoTrack({
        strategy: strategy === 'webcodecs' ? 'ffmpeg' : strategy,
        hwSource: source,
        hwEncode: hw?.encodeViaHwDecode ?? null,
        decoder,
        file: opts.file,
        info: opts.info,
        muxer,
        profile,
        videoCodec,
        bitrate,
        bitrateMode: opts.encode?.bitrateMode ?? 'variable',
        toneMap: opts.encode?.toneMap ?? null,
        rotation,
        trim,
        width,
        height,
        framerate: opts.info.video?.fps ?? 30,
        signal: opts.signal,
        onFrame: (tsUs, durUs) => {
          videoFrames += 1;
          if (tsUs > lastVideoTimestampUs) lastVideoTimestampUs = tsUs;
          lastVideoDurationUs = durUs;
        },
        onProgress: reportProgress,
        onHardware: (accelerated) => { hardwareAccelerated = accelerated; },
      });
      return { blob: muxer.finalize(), encoderResult };
    };

    let attempt: Awaited<ReturnType<typeof encodeOnce>>;
    let ranStrategy: WebCodecsDecodeStrategy = hwSource ? 'webcodecs' : strategy === 'webcodecs' ? 'ffmpeg' : strategy;
    try {
      attempt = await encodeOnce(hwSource);
    } catch (err) {
      if (!hwSource || (err as Error)?.name !== 'HwDecodeFailedError' || opts.signal?.aborted) throw err;
      // The browser's decoder gave up partway (a profile it advertised but
      // can't handle, a GPU reset). Start the video over on Path B; the
      // audio is already extracted.
      console.warn('[wave] hardware decode failed, redoing the video with ffmpeg decode', err);
      hwSource.input.dispose();
      hwSource = null;
      ranStrategy = 'ffmpeg';
      if (opts.streamTarget) await opts.streamTarget.truncate(0);
      attempt = await encodeOnce(null);
    }
    const { blob, encoderResult } = attempt;

    // A/V sync check. Video duration is derived from the highest PTS the
    // encoder actually saw (plus that frame's own duration), so it reflects
    // the real muxed track length rather than a frame-count × 1/fps
    // estimate that would overshoot for VFR sources or scaled-playback
    // captureStream inputs. Audio duration comes from AAC access units at
    // 1024 samples each. Drift > 200 ms is perceptible ("lips lag the
    // sound"); anything less is normal codec-boundary rounding.
    const warnings: string[] = [];
    if (audioInfo && videoFrames > 0) {
      const videoDurationMs = (lastVideoTimestampUs + lastVideoDurationUs) / 1000;
      const audioDurationMs =
        (audioInfo.frames.length * 1024 * 1000) / audioInfo.sampleRate;
      const driftMs = Math.abs(videoDurationMs - audioDurationMs);
      if (driftMs > 200) {
        const which = videoDurationMs > audioDurationMs ? 'video is longer' : 'audio is longer';
        warnings.push(
          `Audio/video drift of ${Math.round(driftMs)} ms detected (${which}). Playback may be slightly out of sync.`
        );
      }
    }

    return {
      blob,
      profile: encoderResult.profile,
      hardwareAccelerated,
      videoFrames,
      decodeStrategy: ranStrategy,
      audioFrames: audioInfo?.frames.length ?? 0,
      warnings,
    };
  } finally {
    opts.signal?.removeEventListener('abort', abortDecoder);
    decoder?.close();
    hwSource?.input.dispose();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Encoder settings
// ─────────────────────────────────────────────────────────────────────────────

const PROFILE_PREFIX = {
  high: 'avc1.6400',
  main: 'avc1.4D40',
  baseline: 'avc1.42E0',
} as const;

// H.264 levels: [level_idc, max macroblocks per frame, max macroblocks per
// second] from Table A-1 of the spec. Encoders refuse a frame larger than the
// level in the codec string allows, so a 4K encode labelled level 4.0 fails.
const AVC_LEVELS: Array<[number, number, number]> = [
  [0x1e, 1620, 40500],
  [0x1f, 3600, 108000],
  [0x20, 5120, 216000],
  [0x28, 8192, 245760],
  [0x2a, 8704, 522240],
  [0x32, 22080, 589824],
  [0x33, 36864, 983040],
  [0x34, 36864, 2073600],
  [0x3c, 139264, 4177920],
];

// HEVC Main-tier levels: [general_level_idc, max luma picture size, max luma
// samples per second].
const HEVC_LEVELS: Array<[number, number, number]> = [
  [120, 2228224, 66846720],
  [123, 2228224, 133693440],
  [150, 8912896, 267386880],
  [153, 8912896, 534773760],
  [156, 8912896, 1069547520],
  [180, 35651584, 1069547520],
];

/** WebCodecs codec string for a profile, with the lowest level that fits the
 *  frame size and rate. */
function codecString(p: EncodeProfile, width: number, height: number, fps: number): string {
  if (p === 'hevcMain10') {
    const ps = width * height;
    const level = HEVC_LEVELS.find(([, maxPs, maxSr]) => ps <= maxPs && ps * fps <= maxSr)?.[0] ?? 180;
    return `hev1.2.4.L${level}.B0`;
  }
  const mbs = Math.ceil(width / 16) * Math.ceil(height / 16);
  const level = AVC_LEVELS.find(([, maxFs, maxMbps]) => mbs <= maxFs && mbs * fps <= maxMbps)?.[0] ?? 0x3c;
  return PROFILE_PREFIX[p] + level.toString(16).toUpperCase().padStart(2, '0');
}

type H264Profile = 'baseline' | 'main' | 'high';
type HevcProfile = 'hevcMain10';
type EncodeProfile = H264Profile | HevcProfile;

function pickProfile(s: WebCodecsSupport, videoCodec: 'h264' | 'hevc'): EncodeProfile {
  if (videoCodec === 'hevc') return 'hevcMain10';
  if (s.h264High) return 'high';
  if (s.h264Main) return 'main';
  return 'baseline';
}

/** Bitrate curve matched to Wave's mpeg4 qscale curve: higher-resolution
 *  sources deserve more bits per pixel, and the shape scales cleanly across
 *  common source sizes. */
function pickBitrate(info: MediaInfo): number {
  const w = info.video?.width ?? 1280;
  const h = info.video?.height ?? 720;
  const fps = info.video?.fps ?? 30;
  const shortEdge = Math.min(w, h);
  const bpp =
    shortEdge >= 2160 ? 0.10 :
    shortEdge >= 1440 ? 0.09 :
    shortEdge >= 1080 ? 0.08 :
    shortEdge >= 720  ? 0.07 :
    shortEdge >= 480  ? 0.08 : 0.10;
  const rate = Math.round(w * h * fps * bpp);
  // Clamp so we never dip below a floor that looks terrible or fly above a
  // ceiling that wastes bytes on a source the encoder can't feed that fast.
  return Math.min(50_000_000, Math.max(400_000, rate));
}

function normaliseRotation(deg: number): 0 | 90 | 180 | 270 {
  const n = ((Math.round(deg) % 360) + 360) % 360;
  if (n === 90 || n === 180 || n === 270) return n;
  return 0;
}

function abortError(): Error {
  const e = new Error('Cancelled');
  e.name = 'AbortError';
  return e;
}

// ─────────────────────────────────────────────────────────────────────────────
// HTMLVideoElement decoder
// ─────────────────────────────────────────────────────────────────────────────

interface VideoDecoderHandle {
  video: HTMLVideoElement;
  objectUrl: string;
  close: () => void;
}

async function openVideoDecoder(file: File, signal?: AbortSignal): Promise<VideoDecoderHandle> {
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  // `volume = 0` (not `muted = true`) is critical. Chrome's background-media
  // power saver auto-pauses "muted, video-only" elements mid-play the moment
  // the element isn't in the viewport (or the tab isn't the fronted one),
  // which fires as `AbortError: The play() request was interrupted because
  // video-only background media was paused to save power.` A silent-but-not-
  // muted element carries an audible audio track from the browser's point
  // of view, so it doesn't hit that pause path. Wave's convert step always
  // starts from a user gesture (the file drop/browse), so autoplay policy
  // is satisfied even without `muted = true`.
  video.muted = false;
  video.volume = 0;
  video.playsInline = true;
  video.preload = 'auto';
  video.crossOrigin = 'anonymous';
  video.src = objectUrl;
  // Keep the element in the viewport at a single opaque-but-invisible
  // pixel. `requestVideoFrameCallback` only fires while the browser paints
  // frames to a composited layer — an off-screen element (position: fixed;
  // left: -10000px) has no compositor output, so the callback never runs
  // and the encode never advances. A 1×1 pixel at the top-left corner with
  // opacity 0.01 is painted (fires rVFC) yet visually invisible to the user.
  video.style.position = 'fixed';
  video.style.left = '0';
  video.style.top = '0';
  video.style.width = '1px';
  video.style.height = '1px';
  video.style.opacity = '0.01';
  video.style.pointerEvents = 'none';
  video.style.zIndex = '-1';
  document.body.appendChild(video);

  const close = () => {
    try { video.pause(); } catch { /* ignore */ }
    video.removeAttribute('src');
    video.load();
    if (video.parentNode) video.parentNode.removeChild(video);
    URL.revokeObjectURL(objectUrl);
  };

  try {
    await new Promise<void>((resolve, reject) => {
      const onLoaded = () => { cleanup(); resolve(); };
      const onError = () => {
        cleanup();
        reject(new WebCodecsUnsupportedError(
          `The browser can't decode this source natively (${file.name}).`
        ));
      };
      const onAbort = () => { cleanup(); reject(abortError()); };
      const cleanup = () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeEventListener('error', onError);
        signal?.removeEventListener('abort', onAbort);
      };
      video.addEventListener('loadedmetadata', onLoaded, { once: true });
      video.addEventListener('error', onError, { once: true });
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  } catch (err) {
    close();
    throw err;
  }

  // A video element that reports zero dimensions after loadedmetadata means
  // the browser accepted the container but can't render the video track —
  // typical for a codec it recognises by MIME but doesn't have a decoder for.
  if (!video.videoWidth || !video.videoHeight) {
    close();
    throw new WebCodecsUnsupportedError('The browser accepted the container but produced no frames.');
  }
  if (typeof video.requestVideoFrameCallback !== 'function') {
    close();
    throw new WebCodecsUnsupportedError('requestVideoFrameCallback is not available in this browser.');
  }

  return { video, objectUrl, close };
}

// ─────────────────────────────────────────────────────────────────────────────
// Video encode loop
// ─────────────────────────────────────────────────────────────────────────────

interface EncodeVideoTrackOptions {
  strategy: WebCodecsDecodeStrategy;
  /** Path C source and encode loop; when set, the `ffmpeg` strategy decodes
   *  with the browser instead of ffmpeg-wasm. */
  hwSource: import('./hwdecode').HwDecodeSource | null;
  hwEncode: typeof import('./hwdecode').encodeViaHwDecode | null;
  decoder: VideoDecoderHandle | null;
  file: File;
  info: MediaInfo;
  muxer: Mp4MuxHandle;
  profile: EncodeProfile;
  videoCodec: 'h264' | 'hevc';
  bitrate: number;
  /** 'constant' when aiming at a file size: the encoder holds the rate
   *  instead of spending extra bits on hard scenes. */
  bitrateMode: 'variable' | 'constant';
  toneMap: 'pq' | 'hlg' | null;
  /** Total clockwise rotation to bake into the pixels (Path B). */
  rotation: 0 | 90 | 180 | 270;
  trim: { start: number; end: number } | null;
  width: number;
  height: number;
  framerate: number;
  signal?: AbortSignal;
  /** Called after each frame is handed to the encoder. `timestampUs` and
   *  `durationUs` are the values on the `VideoFrame`, in microseconds —
   *  the outer transcode uses them for a real-PTS-based A/V sync check. */
  onFrame: (timestampUs: number, durationUs: number) => void;
  onProgress: (fraction: number) => void;
  onHardware: (hw: boolean) => void;
}

async function encodeVideoTrack(
  opts: EncodeVideoTrackOptions
): Promise<{ profile: EncodeProfile }> {
  const { muxer, bitrate, width, height, framerate } = opts;
  const video = opts.decoder?.video;

  // Falls back through profiles if the first one refuses at construction
  // time — Safari for example accepts High in isConfigSupported but sometimes
  // rejects at configure() on iPad. Baseline is our final safety net.
  // HEVC has a single Main-10 candidate; if that refuses, the encoder is
  // just not there and we let the WebCodecsUnsupportedError bubble up so
  // convert.ts falls through to ffmpeg mpeg4.
  let chosenProfile: EncodeProfile = opts.profile;
  let encoder: VideoEncoder | null = null;
  const attempts: EncodeProfile[] = opts.videoCodec === 'hevc'
    ? ['hevcMain10']
    : [
        opts.profile as H264Profile,
        ...(opts.profile !== 'main' ? ['main' as const] : []),
        ...(opts.profile !== 'baseline' ? ['baseline' as const] : []),
      ];

  const encodeErrors: unknown[] = [];
  const pendingChunks: Array<{ chunk: EncodedVideoChunk; meta?: EncodedVideoChunkMetadata }> = [];
  const output = (chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void => {
    pendingChunks.push({ chunk, meta });
  };
  const error = (e: unknown): void => { encodeErrors.push(e); };

  // A constant-rate request is retried as variable when an encoder doesn't
  // offer constant mode, rather than giving up on the profile.
  const modes: Array<'variable' | 'constant'> = opts.bitrateMode === 'constant' ? ['constant', 'variable'] : ['variable'];
  for (const p of attempts) {
    for (const bitrateMode of modes) {
      const candidate = new VideoEncoder({ output, error });
      try {
        const codec = codecString(p, width, height, framerate);
        const isHevc = p === 'hevcMain10';
        const config: VideoEncoderConfig = {
          codec,
          width,
          height,
          bitrate,
          bitrateMode,
          framerate,
          // Container-format hint the encoder writes the decoder-config bytes
          // into: 'avc'/'hevc' produce the length-prefixed NALU stream that
          // mp4-muxer expects for MP4; 'annexb' would give us start-code
          // framing (useful for MPEG-TS but not here).
          ...(isHevc
            ? { hevc: { format: 'hevc' } }
            : { avc: { format: 'avc' } }),
        };
        // Chrome treats 'prefer-hardware' as hardware-only: with no GPU
        // encoder (some PCs, VMs, Linux, or a GPU process that has reset) the
        // configure succeeds and the first encode fails with "Encoder
        // creation error". Ask first, and use the software encoder otherwise.
        const hardware = (await VideoEncoder.isConfigSupported({ ...config, hardwareAcceleration: 'prefer-hardware' })).supported;
        if (!hardware && !(await VideoEncoder.isConfigSupported(config)).supported) {
          throw new Error(`${codec} ${width}×${height} is not supported by this browser's encoder.`);
        }
        candidate.configure({ ...config, hardwareAcceleration: hardware ? 'prefer-hardware' : 'no-preference' });
        opts.onHardware(!!hardware);
        encoder = candidate;
        chosenProfile = p;
        break;
      } catch (e) {
        encodeErrors.push(e);
        try { candidate.close(); } catch { /* ignore */ }
      }
    }
    if (encoder) break;
  }
  if (!encoder) {
    throw new WebCodecsUnsupportedError(
      `VideoEncoder refused every ${opts.videoCodec.toUpperCase()} profile: ${encodeErrors.map(String).join('; ')}`
    );
  }

  const flushChunks = (): void => {
    for (const { chunk, meta } of pendingChunks) muxer.addVideoChunk(chunk, meta);
    pendingChunks.length = 0;
  };

  try {
    if (opts.strategy === 'ffmpeg' && opts.hwSource && opts.hwEncode) {
      await opts.hwEncode({
        source: opts.hwSource, encoder, width, height, framerate,
        // Decoded frames carry no rotation, so the total turn (the source's
        // own tag plus any the user added) goes in here.
        rotation: opts.rotation,
        trim: opts.trim,
        keyframeIntervalFrames: KEYFRAME_INTERVAL_FRAMES,
        encodeQueueHigh: ENCODE_QUEUE_HIGH,
        awaitDrain,
        onFrame: opts.onFrame, onProgress: opts.onProgress,
        signal: opts.signal, flushChunks,
      });
    } else if (opts.strategy === 'ffmpeg') {
      const totalSeconds = opts.info.durationSeconds ?? 0;
      const spanSeconds = opts.trim ? opts.trim.end - opts.trim.start : totalSeconds;
      const durationUs = spanSeconds > 0 ? Math.round(spanSeconds * 1e6) : null;
      await encodeViaFfmpegDecode({
        file: opts.file, encoder, width, height, framerate,
        sourceWidth: opts.info.video?.displayWidth ?? opts.info.video?.width ?? width,
        sourceHeight: opts.info.video?.height ?? height,
        totalSeconds, durationUs,
        trim: opts.trim,
        toneMap: opts.toneMap,
        // Path B pipes raw yuv through ffmpeg's `-vf` chain. ffmpeg's
        // autorotate already turns the frames by the source's own rotation
        // tag, so only the rotation the user added on top needs a `transpose`.
        rotationDeg: normaliseRotation(opts.rotation - normaliseRotation(opts.info.video?.rotationDegrees ?? 0)),
        // Interlaced sources (old DVDs, VOB, ATSC captures) get a yadif=1
        // pass so the encoder never sees combing artefacts. Cheap on the
        // wasm decode leg since ffmpeg is already looking at every field.
        interlaced: !!opts.info.video?.interlaced,
        keyframeIntervalFrames: KEYFRAME_INTERVAL_FRAMES,
        onFrame: opts.onFrame, onProgress: opts.onProgress,
        signal: opts.signal, flushChunks,
        rawStreamFormat: opts.info.rawStreamFormat,
      });
    } else {
      if (!video) throw new Error('WebCodecs browser strategy: decoder handle missing.');
      const duration = video.duration && isFinite(video.duration) ? video.duration : null;
      const durationUs = duration ? Math.round(duration * 1e6) : null;
      // Codec-aware playback rate. captureStream + MediaStreamTrackProcessor
      // delivers frames on wall-clock cadence; any decoded frame Chrome can't
      // emit in time is silently dropped. VP8/VP9 in particular were losing
      // ~45% of frames in QA even at 2× rate — Chrome's VP9 decode + our
      // capture path can't sustain the multiplier reliably. Playing them at
      // realtime keeps every frame; the small size penalty (each conversion
      // takes as long as the clip) is worth a correct output.
      const srcCodec = (opts.info.video?.codec ?? '').toLowerCase();
      const isVpx = srcCodec === 'vp8' || srcCodec === 'vp9';
      video.playbackRate = isVpx ? 1 : MAX_PLAYBACK_RATE;

      // Prefer MediaStreamTrackProcessor when available (Chrome / Edge). It
      // delivers `VideoFrame`s directly from the media pipeline — no
      // canvas.drawImage round trip, no `requestVideoFrameCallback` (which
      // silently no-ops in some headless / off-screen contexts). Falls back
      // to rVFC + canvas on browsers that don't ship MSTP (Safari today).
      const vidWithCapture = video as HTMLVideoElement & { captureStream?: () => MediaStream };
      const useMSTP = typeof (globalThis as { MediaStreamTrackProcessor?: unknown }).MediaStreamTrackProcessor !== 'undefined'
        && typeof vidWithCapture.captureStream === 'function';

      if (useMSTP) {
        await encodeViaTrackProcessor({
          video, encoder, keyframeIntervalFrames: KEYFRAME_INTERVAL_FRAMES,
          durationUs, onFrame: opts.onFrame, onProgress: opts.onProgress,
          signal: opts.signal, flushChunks,
        });
      } else {
        await encodeViaRvfc({
          video, width, height, encoder, framerate,
          keyframeIntervalFrames: KEYFRAME_INTERVAL_FRAMES,
          durationUs, onFrame: opts.onFrame, onProgress: opts.onProgress,
          signal: opts.signal, flushChunks,
        });
      }
    }
  } catch (err) {
    // A failed run must not leave a hardware encoder session open, or the
    // Path B retry may not get one.
    try { encoder.close(); } catch { /* already closed */ }
    // An encoder error closes the codec, and the next encode() then throws
    // "closed codec" - report the error that caused it instead.
    if (encodeErrors.length && (err as Error)?.name === 'InvalidStateError') {
      throw new Error(`VideoEncoder error: ${encodeErrors.map(String).join('; ')}`);
    }
    throw err;
  }

  await encoder.flush();
  flushChunks();
  encoder.close();

  if (encodeErrors.length) {
    throw new Error(`VideoEncoder emitted ${encodeErrors.length} error(s): ${encodeErrors.map(String).join('; ')}`);
  }
  opts.onProgress(1);
  return { profile: chosenProfile };
}

interface EncodeSharedOptions {
  video: HTMLVideoElement;
  encoder: VideoEncoder;
  keyframeIntervalFrames: number;
  durationUs: number | null;
  onFrame: (timestampUs: number, durationUs: number) => void;
  onProgress: (fraction: number) => void;
  signal?: AbortSignal;
  flushChunks: () => void;
}

async function encodeViaTrackProcessor(
  opts: EncodeSharedOptions
): Promise<void> {
  const { video, encoder } = opts;
  const vidWithCapture = video as HTMLVideoElement & { captureStream: () => MediaStream };
  const stream = vidWithCapture.captureStream();
  const videoTrack = stream.getVideoTracks()[0];
  if (!videoTrack) {
    throw new WebCodecsUnsupportedError('captureStream did not expose a video track.');
  }
  // MediaStreamTrackProcessor's constructor lives outside DOM lib for now; the
  // ambient shim at the bottom of the file gives it a minimal type.
  const MSTP = (globalThis as { MediaStreamTrackProcessor: MediaStreamTrackProcessorCtor }).MediaStreamTrackProcessor;
  const processor = new MSTP({ track: videoTrack });
  const reader = processor.readable.getReader();

  let frameIndex = 0;
  let firstTsUs: number | null = null;
  // Muxer requires monotonic PTS on every track. Pathological MPEG-TS
  // captures / restarted streams can produce backward jumps; captureStream
  // usually smooths these but not always. Skip any frame that would
  // regress from the last one we already sent to the encoder.
  let lastEncodedRebasedUs = -1;
  const onAbort = () => { reader.cancel().catch(() => { /* ignore */ }); };
  opts.signal?.addEventListener('abort', onAbort, { once: true });

  try {
    await video.play();
    // Loop until captureStream ends (video reached end and browser closes
    // track). Every frame we pull from the reader MUST be closed before
    // the loop advances — VideoFrames hold GPU memory and the browser
    // stalls the pipeline if we let too many leak. A single try/finally
    // around each read guarantees close even when encode() throws.
    while (true) {
      if (opts.signal?.aborted) throw abortError();
      const { value: frame, done } = await reader.read();
      if (done) break;
      if (!frame) continue;
      let rebasedFrame: VideoFrame | null = null;
      try {
        if (firstTsUs === null) firstTsUs = frame.timestamp;
        const rebased = frame.timestamp - firstTsUs;
        if (rebased <= lastEncodedRebasedUs) {
          // Non-monotonic PTS — drop and continue. The muxer would reject
          // the whole track if we sent a backward-going timestamp, so
          // silently discarding is the safest choice for pathological
          // captures.
          continue;
        }
        // Duration cap: captureStream sometimes emits a handful of frames
        // whose rebased timestamp lands past the source's own duration
        // (roughly, the browser flushing its decode ring after `ended`
        // fires). Left in, they extend the video track ~500–800 ms past
        // the audio track and users see the last frame linger after the
        // audio stops. Drop anything beyond the source duration to keep
        // A/V lengths aligned.
        if (opts.durationUs && rebased > opts.durationUs) {
          continue;
        }
        rebasedFrame = new VideoFrame(frame, { timestamp: rebased });
        encoder.encode(rebasedFrame, { keyFrame: frameIndex % opts.keyframeIntervalFrames === 0 });
        frameIndex += 1;
        lastEncodedRebasedUs = rebased;
        opts.onFrame(rebased, rebasedFrame.duration ?? 0);
        if (opts.durationUs) opts.onProgress(Math.min(0.999, rebased / opts.durationUs));
      } finally {
        try { frame.close(); } catch { /* ignore */ }
        if (rebasedFrame) { try { rebasedFrame.close(); } catch { /* ignore */ } }
      }
      opts.flushChunks();

      if (encoder.encodeQueueSize > ENCODE_QUEUE_HIGH) {
        video.pause();
        await awaitDrain(encoder);
        if (!opts.signal?.aborted) video.play().catch(() => { /* ignore */ });
      }
      if (video.ended) {
        // captureStream can keep the track open after `ended`; give the reader
        // one more tick to deliver any final buffered frames, then exit.
        await new Promise((r) => setTimeout(r, 40));
        try { videoTrack.stop(); } catch { /* ignore */ }
      }
    }
  } finally {
    opts.signal?.removeEventListener('abort', onAbort);
    try { reader.releaseLock(); } catch { /* ignore */ }
    try { videoTrack.stop(); } catch { /* ignore */ }
  }
}

interface EncodeRvfcOptions extends EncodeSharedOptions {
  width: number;
  height: number;
  framerate: number;
}

async function encodeViaRvfc(opts: EncodeRvfcOptions): Promise<void> {
  const { video, width, height, encoder, framerate } = opts;
  const canvas = createCanvas(width, height);
  // Typed explicitly: with the canvas a union, TypeScript can resolve
  // getContext to the generic overload that includes WebGL contexts.
  const ctx = canvas.getContext('2d', { willReadFrequently: false, alpha: false }) as
    CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) throw new Error('WebCodecs: 2D canvas context unavailable.');

  let frameIndex = 0;
  let lastTimestampUs = -1;
  let lastFrameDurationUs = Math.round(1e6 / (framerate || 30));
  let ended = false;
  const encodeErrors: unknown[] = [];

  const drainedPromise = new Promise<void>((resolve, reject) => {
    const onError = () => reject(new Error('Video element error mid-encode.'));
    const onEnded = () => { ended = true; resolve(); };
    const onAbort = () => reject(abortError());
    video.addEventListener('error', onError, { once: true });
    video.addEventListener('ended', onEnded, { once: true });
    opts.signal?.addEventListener('abort', onAbort, { once: true });
  });

  const handleFrame = (_nowMs: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata): void => {
    if (opts.signal?.aborted || ended) return;
    const mediaTimeUs = Math.round(metadata.mediaTime * 1e6);
    if (mediaTimeUs <= lastTimestampUs) {
      video.requestVideoFrameCallback(handleFrame);
      return;
    }
    // Same duration cap as the MSTP path: rVFC occasionally fires for
    // frames past the source's stated duration when the decoder is
    // flushing. Drop them so the output video track ends when the
    // source (and the audio) does.
    if (opts.durationUs && mediaTimeUs > opts.durationUs) {
      video.requestVideoFrameCallback(handleFrame);
      return;
    }
    if (lastTimestampUs >= 0) {
      lastFrameDurationUs = Math.max(1, mediaTimeUs - lastTimestampUs);
    }
    lastTimestampUs = mediaTimeUs;
    try { ctx.drawImage(video, 0, 0, width, height); } catch {
      video.requestVideoFrameCallback(handleFrame);
      return;
    }
    const frame = new VideoFrame(canvas, { timestamp: mediaTimeUs, duration: lastFrameDurationUs });
    try {
      encoder.encode(frame, { keyFrame: frameIndex % opts.keyframeIntervalFrames === 0 });
    } catch (e) { encodeErrors.push(e); }
    frame.close();
    frameIndex += 1;
    opts.onFrame(mediaTimeUs, lastFrameDurationUs);
    if (opts.durationUs) opts.onProgress(Math.min(0.999, mediaTimeUs / opts.durationUs));
    opts.flushChunks();
    if (encoder.encodeQueueSize > ENCODE_QUEUE_HIGH) {
      video.pause();
      void awaitDrain(encoder).then(() => {
        if (!ended && !opts.signal?.aborted) video.play().catch(() => { /* ignore */ });
      });
    }
    video.requestVideoFrameCallback(handleFrame);
  };
  video.requestVideoFrameCallback(handleFrame);
  await video.play();
  await drainedPromise;
  if (encodeErrors.length) throw new Error(String(encodeErrors[0]));
}

interface EncodeFfmpegDecodeOptions {
  file: File;
  encoder: VideoEncoder;
  /** Display dimensions the encoder is configured for (post-rotation). */
  width: number;
  height: number;
  /** Source frame size, to tell a downscale from a plain format change. */
  sourceWidth: number;
  sourceHeight: number;
  framerate: number;
  totalSeconds: number;
  durationUs: number | null;
  /** Extra clockwise rotation (0/90/180/270) on top of the source's own tag,
   *  which ffmpeg's autorotate has already applied. Non-zero adds a
   *  `transpose` filter. */
  rotationDeg: number;
  /** Section of the source to decode, in seconds. Output timestamps start
   *  at zero at `trim.start`. */
  trim?: { start: number; end: number } | null;
  /** HDR transfer to tone-map down to SDR before the 8-bit encode. */
  toneMap?: 'pq' | 'hlg' | null;
  /** Source is interlaced (tt / bb field order). Triggers `yadif=1` before
   *  the raw-yuv output so the encoder never sees combing artefacts. */
  interlaced: boolean;
  keyframeIntervalFrames: number;
  onFrame: (timestampUs: number, durationUs: number) => void;
  onProgress: (fraction: number) => void;
  signal?: AbortSignal;
  flushChunks: () => void;
  /** Sniffed raw-stream format from MediaInfo, if any. Injected as `-f`
   *  before every `-i` in the segmented decode loop. Never derived from the
   *  filename here — that path was the "wrong-extension crashes ffmpeg" bug. */
  rawStreamFormat: string | null;
}

/** Target raw-yuv420p output size per ffmpeg segment call. Kept small so a
 *  4K source doesn't blow past the wasm heap; 128 MB is well under the
 *  512 MB @ffmpeg/core budget with headroom for the rest of the pipeline. */
const FFMPEG_DECODE_SEGMENT_BYTES = 128 * 1024 * 1024;
/** Hard bounds on segment length. Very short segments amplify per-call
 *  ffmpeg overhead; very long ones risk memory pressure at high resolution. */
const FFMPEG_DECODE_MIN_SEGMENT_S = 0.5;
const FFMPEG_DECODE_MAX_SEGMENT_S = 8;

async function encodeViaFfmpegDecode(opts: EncodeFfmpegDecodeOptions): Promise<void> {
  const { encoder, width, height, framerate, totalSeconds } = opts;
  // totalSeconds may be 0 when the source doesn't advertise a duration
  // (some live-capture MKVs, screen recorders that don't finalise). The
  // loop below walks segments until one produces zero frames — no
  // duration needed. Progress reporting downgrades to indeterminate.
  const durationKnown = totalSeconds > 0;
  // HDR sources are tone-mapped on the GPU when WebGL2 is there: ffmpeg hands
  // over 10-bit frames and the shader does the rest. Without WebGL2, ffmpeg's
  // own (much slower) float filters do it and the frames stay 8-bit.
  const gpuToneMap = opts.toneMap ? createGpuToneMapper(width, height, opts.toneMap) : null;
  const bytesPerSample = gpuToneMap ? 2 : 1;
  // yuv420p (I420) planar, or yuv420p10le with 16-bit samples for the GPU path.
  const bytesPerFrame = Math.ceil(width * height * 1.5) * bytesPerSample;
  const bytesPerSecond = bytesPerFrame * framerate;
  // Segment length is snapped to a whole number of frames. A fractional
  // length (128 MB / 93 MB/s = 1.37 s at 1080p30) puts every boundary
  // mid-frame, so the `fps` filter emits a frame on both sides of it and the
  // output gained a duplicate-timestamp frame per boundary.
  const segmentFrames = Math.max(1, Math.round(Math.max(
    FFMPEG_DECODE_MIN_SEGMENT_S,
    Math.min(FFMPEG_DECODE_MAX_SEGMENT_S, FFMPEG_DECODE_SEGMENT_BYTES / Math.max(1, bytesPerSecond)),
  ) * framerate));
  const segmentSeconds = segmentFrames / framerate;
  const frameDurationUs = Math.round(1e6 / (framerate || 30));

  const ext = opts.file.name.slice(Math.max(0, opts.file.name.lastIndexOf('.') + 1)).toLowerCase() || 'bin';
  const mountName = `wc-vd.${ext}`;
  const rawName = 'wc-vd.yuv';
  const rawFmt = opts.rawStreamFormat;
  const mount = await mountInputFile(opts.file, mountName);
  const srcPixels = (opts.sourceWidth || width) * (opts.sourceHeight || height);
  const downscaled = width * height < srcPixels * 0.9;

  let frameIndex = 0;
  let cursor = 0; // seconds into source we've already yielded
  // Muxer needs strictly monotonic PTS. Because we use input-seek (-ss
  // before -i), consecutive segments can occasionally overlap by a keyframe
  // or two — ffmpeg trims most of that but not always. Track the highest
  // timestamp we've handed to the encoder and skip any frame that would
  // regress across a segment boundary.
  let lastEncodedTimestampUs = -1;
  // Cap the walk at some finite value even for unknown-duration sources so
  // a broken container can't spin forever. 12 hours is far past any real
  // Wave input.
  const walkCeiling = opts.trim ? opts.trim.end : durationKnown ? totalSeconds : 12 * 3600;
  const endKnown = durationKnown || !!opts.trim;
  // A trim starts the walk part-way in; frame timestamps are measured from
  // the trim start so the output begins at zero.
  const origin = opts.trim?.start ?? 0;
  cursor = origin;
  try {
    while (cursor < walkCeiling - 1e-3) {
      if (opts.signal?.aborted) throw abortError();
      const segDur = endKnown
        ? Math.min(segmentSeconds, walkCeiling - cursor)
        : segmentSeconds;
      // Input-seek (-ss before -i) is fast: ffmpeg jumps to the nearest
      // keyframe before `ss`, decodes forward, then trims to `t`. That's
      // exactly what we want per-segment. We don't care about audio here
      // (audio is extracted separately in extractAacAudio).
      // Filter chain (in order): yadif de-interlace (only when the source
      // is actually interlaced), transpose to bake rotation, scale to the
      // configured encode dimensions, force a constant frame rate. When a
      // step would be a no-op we omit it to save a pass through the frame.
      const filters: string[] = [];
      if (opts.interlaced) filters.push('yadif=1');
      filters.push(...transposeFilters(normaliseRotation(opts.rotationDeg)));
      // fast_bilinear ignores the downscale ratio and aliases on big
      // reductions; plain bilinear widens its taps to match.
      filters.push(`scale=${width}:${height}:flags=${downscaled ? 'bilinear' : 'fast_bilinear'}`);
      // Tone mapping runs after the scale so a downscaled encode only pays
      // for the pixels it keeps.
      if (opts.toneMap && !gpuToneMap) filters.push(toneMapFilter(opts.toneMap));
      filters.push(`fps=${framerate}`);
      const args = [
        '-hide_banner',
        '-fflags', '+genpts+igndts',
        '-ss', cursor.toFixed(3),
        ...(rawFmt ? ['-f', rawFmt] : []),
        '-i', mount.path,
        '-t', segDur.toFixed(3),
        '-an',
        '-map', '0:v:0',
        '-vf', filters.join(','),
        '-pix_fmt', gpuToneMap ? 'yuv420p10le' : 'yuv420p',
        '-f', 'rawvideo',
        rawName,
      ];
      const { code, logs } = await runFFmpeg(args, { signal: opts.signal });
      if (opts.signal?.aborted) throw abortError();
      if (code !== 0) {
        const tail = logs.slice(-6).join(' | ');
        throw new Error(`ffmpeg decode segment (t=${cursor.toFixed(2)}s) failed with code ${code}. ${tail}`);
      }
      const raw = await readFile(rawName);
      // Free the wasm-side file immediately so we hold the buffer only in
      // JS heap. `readFile` already copied into a JS-owned Uint8Array.
      await deleteFile(rawName);

      const framesInSegment = Math.floor(raw.byteLength / bytesPerFrame);
      if (!framesInSegment) {
        // Zero-frame segment means we've walked past the end of the source
        // (or hit a gap ffmpeg couldn't seek into). When duration is
        // unknown, this is our end-of-stream signal — break out. When it
        // is known, it might just be a broken chunk mid-file; step past
        // it and try the next one.
        if (!endKnown) break;
        cursor += segDur;
        continue;
      }
      for (let i = 0; i < framesInSegment; i++) {
        if (opts.signal?.aborted) throw abortError();
        const offset = i * bytesPerFrame;
        // Snap to the frame grid: the muxer rounds to a 1/fps timescale, so
        // two frames a few µs apart would otherwise collide on one DTS.
        const slot = Math.round((cursor - origin + i / framerate) * framerate);
        const timestampUs = Math.round((slot * 1e6) / framerate);
        // Segment overlap: input-seek can land a couple of frames earlier
        // than the requested start; ignore anything that would go backward.
        if (timestampUs <= lastEncodedTimestampUs) continue;
        // Zero-copy view into the raw buffer for the VideoFrame constructor.
        const view = new Uint8Array(raw.buffer, raw.byteOffset + offset, bytesPerFrame);
        const frame = gpuToneMap
          ? gpuToneMap.frame(view, timestampUs, frameDurationUs)
          : new VideoFrame(view, {
              format: 'I420',
              codedWidth: width,
              codedHeight: height,
              timestamp: timestampUs,
              duration: frameDurationUs,
            });
        try {
          encoder.encode(frame, { keyFrame: frameIndex % opts.keyframeIntervalFrames === 0 });
        } finally {
          frame.close();
        }
        frameIndex += 1;
        lastEncodedTimestampUs = timestampUs;
        opts.onFrame(timestampUs, frameDurationUs);
        if (opts.durationUs) opts.onProgress(Math.min(0.999, timestampUs / opts.durationUs));
        opts.flushChunks();
        if (encoder.encodeQueueSize > ENCODE_QUEUE_HIGH) {
          await awaitDrain(encoder);
        }
      }
      cursor += segDur;
    }
  } finally {
    gpuToneMap?.close();
    try { await deleteFile(rawName); } catch { /* best-effort */ }
    await mount.unmount();
  }
}

async function awaitDrain(encoder: VideoEncoder): Promise<void> {
  // encoder.encodeQueueSize is updated as the encoder consumes frames; we
  // yield in a small loop until it drops. Cheap on modern browsers.
  while (encoder.encodeQueueSize > ENCODE_QUEUE_LOW) {
    await new Promise<void>((r) => setTimeout(r, 8));
  }
}

function createCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  // OffscreenCanvas on the main thread is broadly supported and skips the
  // DOM compositor for the intermediate — small but real gain per frame.
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height) as unknown as HTMLCanvasElement;
  }
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

// ─────────────────────────────────────────────────────────────────────────────
// Audio extraction via ffmpeg-wasm → ADTS AAC → raw AAC frames
// ─────────────────────────────────────────────────────────────────────────────

interface AacExtractOptions {
  trim: { start: number; end: number } | null;
  /** AAC bitrate in kbps; null scales with the channel count. */
  bitrateKbps: number | null;
  /** Channel count; null keeps the source's (up to 5.1). */
  channels: number | null;
}

interface ExtractedAudio {
  sampleRate: number;
  channels: number;
  /** AAC access units + their PTS/duration in microseconds. */
  frames: Array<{ payload: Uint8Array; timestampUs: number; durationUs: number }>;
}

async function extractAacAudio(
  file: File,
  info: MediaInfo,
  signal?: AbortSignal,
  opts: AacExtractOptions = { trim: null, bitrateKbps: null, channels: null },
): Promise<ExtractedAudio | null> {
  if (!info.audio) return null;

  const ext = file.name.slice(Math.max(0, file.name.lastIndexOf('.') + 1)).toLowerCase() || 'bin';
  const mountName = `wc-input.${ext}`;
  const outputName = 'wc-audio.aac';
  const rawFmtAudio = info.rawStreamFormat;
  const mount = await mountInputFile(file, mountName);
  try {
    // Encode audio to AAC ADTS (adts is the raw AAC bitstream in a .aac file).
    // Even when the source is already AAC we re-encode to guarantee ADTS
    // packet framing — copying AAC-in-MP4 to a .aac file needs an aac_adtstoasc
    // filter and gains us nothing in size.
    //
    // Channel handling: AAC-LC supports up to 6 channels (5.1). We preserve
    // the source's channel count when we can and clamp anything larger
    // (7.1 → 5.1). Bitrate scales with channel count — 96 kbps/channel is
    // the LAME rule-of-thumb for transparent AAC.
    const srcCh = info.audio.numChannels ?? 2;
    const outCh = Math.min(6, Math.max(1, opts.channels ?? srcCh));
    const bitrateKbps = opts.bitrateKbps ?? Math.max(96, Math.min(384, outCh * 96));
    // Input-side -ss on a re-encode is sample-accurate: ffmpeg decodes from
    // the preceding packet and drops everything before the cut point, so the
    // audio starts on the same instant as Path B's first video frame.
    const args = [
      '-hide_banner',
      '-fflags', '+genpts+igndts',
      ...(opts.trim ? ['-ss', opts.trim.start.toFixed(3)] : []),
      ...(rawFmtAudio ? ['-f', rawFmtAudio] : []),
      '-i', mount.path,
      ...(opts.trim ? ['-t', (opts.trim.end - opts.trim.start).toFixed(3)] : []),
      '-map', '0:a:0',
      '-vn',
      '-ac', String(outCh),
      '-c:a', 'aac',
      '-b:a', `${bitrateKbps}k`,
      '-aac_coder', 'twoloop',
      '-f', 'adts',
      outputName,
    ];
    const { code, logs } = await runFFmpeg(args, { signal });
    if (code !== 0) {
      const tail = logs.slice(-20).join('\n');
      throw new Error(`Audio extract failed (code ${code}). ${tail}`);
    }
    const buffer = await readFile(outputName);
    const detached = new Uint8Array(buffer.byteLength);
    detached.set(buffer);
    return parseAdts(detached);
  } finally {
    await deleteFile(outputName);
    await mount.unmount();
  }
}

function parseAdts(bytes: Uint8Array): ExtractedAudio {
  const frames: ExtractedAudio['frames'] = [];
  let offset = 0;
  let sampleRate = 0;
  let channels = 0;
  let ptsSamples = 0;

  while (offset + 7 <= bytes.length) {
    // Sync word 0xFFF check.
    if (bytes[offset] !== 0xff || (bytes[offset + 1] & 0xf0) !== 0xf0) {
      // Not a sync word — try the next byte. Malformed ADTS is rare from
      // ffmpeg output, but be lenient.
      offset += 1;
      continue;
    }
    const protectionAbsent = bytes[offset + 1] & 0x01;
    // profile bits (2), sampling freq idx (4), private (1), channel_cfg (3)
    const b2 = bytes[offset + 2];
    const b3 = bytes[offset + 3];
    const freqIdx = (b2 >> 2) & 0x0f;
    const chCfgHigh = (b2 & 0x01) << 2;
    const chCfgLow = (b3 >> 6) & 0x03;
    const channelConfig = chCfgHigh | chCfgLow;
    // frame_length: 13 bits across b3, b4, b5
    const frameLength =
      ((b3 & 0x03) << 11) |
      (bytes[offset + 4] << 3) |
      ((bytes[offset + 5] >> 5) & 0x07);
    if (frameLength <= 0 || offset + frameLength > bytes.length) break;

    const headerLen = protectionAbsent ? 7 : 9;
    const payload = bytes.subarray(offset + headerLen, offset + frameLength);

    if (!sampleRate) sampleRate = ADTS_SAMPLE_RATES[freqIdx] ?? 44100;
    if (!channels) channels = channelConfig || 2;

    const samplesPerFrame = 1024;
    const timestampUs = Math.round((ptsSamples * 1_000_000) / sampleRate);
    const durationUs = Math.round((samplesPerFrame * 1_000_000) / sampleRate);
    frames.push({ payload: new Uint8Array(payload), timestampUs, durationUs });
    ptsSamples += samplesPerFrame;
    offset += frameLength;
  }

  if (!frames.length) {
    throw new Error('AAC extract produced no frames.');
  }
  return { sampleRate: sampleRate || 44100, channels: channels || 2, frames };
}

const ADTS_SAMPLE_RATES = [
  96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050,
  16000, 12000, 11025, 8000,  7350,
];

function writeAacFrames(muxer: Mp4MuxHandle, frames: ExtractedAudio['frames']): void {
  for (const f of frames) muxer.addRawAacFrame(f.payload, f.timestampUs, f.durationUs);
}

// Ambient shim for MediaStreamTrackProcessor — DOM lib in TS 5.6 declares a
// stub without the `track` init property, so accessing it as-typed fails.
// Wave uses only the readable stream, which is a `ReadableStream<VideoFrame>`.
interface MediaStreamTrackProcessorCtor {
  new (init: { track: MediaStreamTrack }): { readable: ReadableStream<VideoFrame> };
}

